import { TellescopeModelFromName, useTellescopeInfiniteQuery, useTellescopeQuery } from "@tellescope/tanstack-query";
import { useChatCompositionPanelSearchParams, usePollForInboxThread } from "../../../hooks";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "@tellescope/forms";
import { useTranslation } from "react-i18next";
import { Loader2, X } from "lucide-react";
import { Link } from "../../../../../components";
import { Button, Combobox, ComboboxOption, Input, Label } from "@tellescope/ui";
import { InboxChatRecipientItem } from "./InboxChatRecipientItem";
import { formatName } from "@tellescope/utils";
import { InboxChatCompositionFooter } from "./InboxChatCompositionFooter";

export const InboxChatCompositionPanel = () => {
  const { t } = useTranslation("inbox");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useChatCompositionPanelSearchParams();
  const [enduserSearch, setEnduserSearch] = useState("");
  const [selectedEnduser, setSelectedEnduser] = useState<TellescopeModelFromName<"endusers">>();
  const [isSending, setIsSending] = useState(false);

  const endusersQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      limit: 20,
      search: enduserSearch
        ? {
            query: enduserSearch,
          }
        : undefined,
    },
    select: (data) => data.pages.flat(),
  });

  const pollForThread = usePollForInboxThread({
    maxAttempts: 3,
    initialBackoffMs: 500,
    onMaxAttemptsReached: () => setIsSending(false),
  });

  const editingMessageQuery = useTellescopeQuery({
    model: "chats",
    method: "getOne",
    request: searchParams.draftId ?? "",
    enabled: !!searchParams.draftId,
  });
  const editingMessage = editingMessageQuery.data;
  const isEditingExisting = !!searchParams.draftId && !!searchParams.roomId;

  const form = useForm({
    fields: {
      enduserId: {
        type: "text",
        initialValue: decodeURI(searchParams.enduserId ?? ""),
        required: true,
      },
      subject: {
        type: "text",
        initialValue: decodeURI(searchParams.subject ?? ""),
        required: true,
      },
    },
    validateOn: "blur",
  });

  // Resolve the recipient's name when the panel is seeded from the URL (e.g. "Start Discussion"):
  // the `enduserId` field is populated from the param, but the combobox label needs the record,
  // which the search-limited list above may not contain. Falls back to this direct lookup.
  const currentEnduserId = form.getFieldProps("enduserId").value;
  const { data: currentEnduser } = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: currentEnduserId,
    enabled: !!currentEnduserId,
  });
  const recipientEnduser = selectedEnduser ?? currentEnduser;

  return (
    <div className="relative flex flex-col h-full">
      {/* Loading Overlay */}
      {isSending && (
        <div className="absolute inset-0 bg-background/50 backdrop-blur-[2px] z-50 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <div className="flex flex-col items-center gap-1">
              <p className="text-muted-foreground">{t("Sending...")}</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between px-4 py-2 border-b h-[52px]">
        <h3 className="text-lg font-semibold">
          {isEditingExisting ? (editingMessage?.sendAt ? t("Edit Scheduled Message") : t("Edit Draft")) : t("New Chat")}
        </h3>
        <Link to="..">
          <Button variant="ghost" size="icon">
            <X />
          </Button>
        </Link>
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="h-full space-y-4 flex flex-col justify-between">
          <div className="flex flex-col gap-4 w-full p-4">
            <div className="space-y-2">
              <Label htmlFor="recipient">{t("To")}</Label>
              <Combobox
                value={{
                  value: form.getFieldProps("enduserId").value,
                  label: recipientEnduser ? formatName(recipientEnduser) : "",
                  metadata: { enduserId: form.getFieldProps("enduserId").value },
                }}
                formatSelectedValue={({ label }) => <p>{label}</p>}
                onChange={(newVal) => {
                  if (typeof newVal?.metadata?.enduserId === "string") {
                    form.setFieldValue("enduserId", newVal?.metadata?.enduserId);
                    setSearchParams((prev) => ({
                      ...prev,
                      enduserId: newVal?.metadata?.enduserId ?? "",
                    }));
                    setSelectedEnduser(endusersQuery.data?.find(({ id }) => id === newVal.metadata?.enduserId));
                  } else {
                    form.setFieldValue("enduserId", "");
                    setSearchParams((prev) => ({
                      ...prev,
                      enduserId: "",
                    }));
                    setSelectedEnduser(undefined);
                  }
                }}
                formatOption={(props) => (
                  <InboxChatRecipientItem
                    {...props}
                    enduser={endusersQuery.data?.find((enduser) => enduser.id === props.option.metadata?.enduserId)}
                  />
                )}
                searchQuery={enduserSearch}
                setSearchQuery={setEnduserSearch}
                customFilter={(_, options) => options}
                placeholder={t("Select recipient...")}
                options={
                  endusersQuery.data?.map<ComboboxOption<string>>((enduser) => ({
                    value: enduser.id,
                    label: formatName(enduser),
                    metadata: {
                      enduserId: enduser.id,
                    },
                  })) ?? []
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject">{t("Subject")}</Label>
              <Input
                id="subject"
                type="text"
                placeholder={t("Enter subject")}
                readOnly={isEditingExisting}
                {...form.getFieldProps("subject")}
                onChange={(e) => {
                  form.setFieldValue("subject", e.target.value);
                  setSearchParams((prev) => ({
                    ...prev,
                    subject: e.target.value,
                  }));
                }}
                className="h-9"
              />
            </div>
          </div>
          <InboxChatCompositionFooter
            enduserId={form.values.enduserId}
            subject={form.values.subject}
            onSend={(chatRoom) => {
              setIsSending(true);
              pollForThread({ threadId: chatRoom.id, type: "Chat" });
            }}
            onSaveDraftSuccess={() => navigate("..")}
            editing={
              isEditingExisting && editingMessage
                ? {
                    message: editingMessage,
                    isScheduled: !!editingMessage.sendAt,
                    onDone: () => navigate(".."),
                  }
                : undefined
            }
          />
        </div>
      </div>
    </div>
  );
};
